import { useState } from 'react';
import { cn } from '../utils/styles';

export interface AvatarProps {
  src?: string | null;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeMap = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

const initialsSizeMap = {
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

function PlaceholderFallback({ name, size, className }: { name: string; size: 'sm' | 'md' | 'lg'; className?: string }) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-full bg-sky-50 ring-2 ring-gray-200 shadow-sm',
        sizeMap[size],
        className
      )}
      title={name}
    >
      <img src="/images/default-avatar.png" alt={name} className="absolute inset-0 w-full h-full object-contain opacity-20 p-[18%]" />
      <span className={cn('absolute inset-0 flex items-center justify-center font-bold text-sky-700', initialsSizeMap[size])}>
        {getInitials(name)}
      </span>
    </div>
  );
}

export function Avatar({ src, name, size = 'md', className }: AvatarProps) {
  const [imgError, setImgError] = useState(false);

  if (!src || imgError) {
    return <PlaceholderFallback name={name} size={size} className={className} />;
  }

  return (
    <img
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      className={cn('rounded-full object-cover ring-[1.5px] ring-white/40 shadow-sm', sizeMap[size], className)}
    />
  );
}
